import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import OutcomeModalV2Cst from "@components/OutcomeModalV2Cst";
import UserWizard from "../Wizard/UserWizard";
import { useSaveUser } from "../Wizard/useSaveUser";
import {
  CUSTOM_FIELD_KEYS,
  mapDetailToWizard,
  UserWizardData,
} from "../Wizard/types";
import {
  CustomFieldsCatalog,
  UserCustomFieldsProvider,
} from "../Wizard/customFieldsContext";

const EditUser: React.FC = () => {
  const { id: corporateId = "", userId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("user-wizard-v2");
  const backTo = `/v2/corporate/${corporateId}/detail/users-groups`;
  const [submitting, setSubmitting] = useState(false);

  const { dataDetail, isLoading: isLoadingDetail } = useFetchDetail(
    ServicesURL.customer,
    `${userId}?corporateId=${corporateId}`,
    PagesID["corporate.customers"],
  );

  const { dataDetail: dataCustomFields } = useFetchDataDetail(
    `${ServicesURL.getCorporateCustomFields}?corporate_id=${corporateId}`,
    PagesID["corporate.customFields"],
  );

  const {
    dataDetail: userGroups,
    isLoading: isLoadingGroups,
    mutate: mutateGroups,
  } = useFetchDataDetail(
    corporateId && userId
      ? `${ServicesURL.corporate}/${corporateId}/users/${userId}/groups?type=STATIC`
      : null,
  );

  const initialStaticGroupIds = useMemo(() => {
    const list = Array.isArray(userGroups)
      ? userGroups
      : userGroups?.groups_list || [];
    return list
      .map((g: any) => g?.id)
      .filter((id: any) => id !== undefined && id !== null);
  }, [userGroups]);

  const catalog: CustomFieldsCatalog = useMemo(() => {
    const raw = dataCustomFields?.catalog || {};
    const out: CustomFieldsCatalog = {};
    CUSTOM_FIELD_KEYS.forEach((k) => {
      if (raw[k]) out[k] = raw[k];
    });
    return out;
  }, [dataCustomFields]);

  const initialData = useMemo(
    () => ({
      ...mapDetailToWizard(dataDetail),
      staticGroupIds: initialStaticGroupIds,
    }),
    [dataDetail, initialStaticGroupIds],
  );

  const { outcome, openOutcome, setOpenOutcome, save } = useSaveUser({
    mode: "edit",
    corporateId,
    userId,
    detail: dataDetail,
    initialStaticGroupIds: initialData.staticGroupIds,
  });

  const onComplete = async (data: UserWizardData) => {
    setSubmitting(true);
    try {
      await save(data);
      await mutateGroups?.();
    } finally {
      setSubmitting(false);
    }
  };

  const detailReady =
    !isLoadingDetail && !!dataDetail && !Array.isArray(dataDetail);
  const groupsSettled = isLoadingGroups === false;

  if (!detailReady || !dataCustomFields || !groupsSettled)
    return <FormLoadingStateCst />;

  return (
    <UserCustomFieldsProvider catalog={catalog}>
      <UserWizard
        mode="edit"
        initialData={initialData}
        backTo={backTo}
        onComplete={onComplete}
        isCompleting={submitting}
      />
      <OutcomeModalV2Cst
        open={openOutcome}
        outcome={outcome}
        successTitle={t("outcome.updateSuccessTitle")}
        errorTitle={t("outcome.errorTitle")}
        confirmLabel={t("outcome.goToUsersList")}
        onClose={() => setOpenOutcome(false)}
        onConfirm={() => {
          setOpenOutcome(false);
          navigate(backTo);
        }}
      />
    </UserCustomFieldsProvider>
  );
};

export default EditUser;
